// The deterministic validation gate (§5 of the plan). One pure function used
// in three places — the builder's live badges, the dry-run gate, and the
// final re-check at execute — so the UI and the push logic can never
// disagree. Always validated against LIVE post-options, never a hardcoded
// schema. Errors block; warnings inform.
//
// Pure module: no React, no Supabase, no fetch.

import { normalizeFieldType, fieldTypeLabel } from './fmxCustomFieldsApi.js';
import { normalizeFieldset, fieldsetLabel } from './fieldsets.js';
import { matchNativeField } from './nativeFields.js';
import { screenAgainstExisting, findBatchNameCollisions } from './dedupe.js';
import { validateFormula } from './visibilityFormula.js';

// Behavior flags per field type. /v1/custom-fields/post-options returns all
// flag keys with a flat isPermitted (it is not parameterized by field type),
// so the per-type gating FMX's own UI applies is encoded here. Types not
// listed for a flag reject it.
export const FLAGS_BY_TYPE = {
  allowMultipleLines:     ['text'],
  allowOtherOption:       ['dropDownList'],
  allowMultipleSelections:['dropDownList'],
  disallowNegativeValues: ['number', 'currency'],
};

const OPTION_TYPES = ['dropDownList'];

// Scoping keys: post-options systemFields whose key names an ID association.
function isScopingKey(key) {
  return /IDs?$/.test(key);
}

function findSystemField(postOptions, key) {
  return (postOptions?.systemFields || []).find(sf => sf.key === key) || null;
}

function scopeIdsOf(value) {
  // Scope values arrive as {id,name}, [{id,name}…], a bare id, or an id array.
  if (value === null || value === undefined || value === '') return [];
  const arr = Array.isArray(value) ? value : [value];
  return arr
    .map(v => (v && typeof v === 'object' ? v.id : v))
    .filter(v => v !== null && v !== undefined && v !== '');
}

/**
 * Validate one draft field.
 *
 * @param {object} draft — { id, name, fieldset, fieldType, isRequired,
 *   options, defaults, allowOtherOption, allowMultipleSelections,
 *   allowMultipleLines, disallowNegativeValues, description, sortPosition }
 * @param {object} ctx
 *   confirmedFieldsets  — array of fieldset keys confirmed on the scope screen
 *   postOptionsByFieldset — { [fieldset]: { systemFields } } from the LIVE
 *                           GET /v1/custom-fields/post-options?fieldset=X
 *   scope               — { [fieldset]: { [scopingKey]: value } } from the
 *                         scope screen ({id,name} shapes or bare ids)
 *   existingFieldsByFieldset — { [fieldset]: [read-model rows] } for the
 *                              duplicate screen
 *   nativeLabelsByFieldset   — { [fieldset]: [labels] } live system-field
 *                              labels of the TARGET entity form (optional)
 * @returns {{ errors: string[], warnings: string[] }} plain-language messages
 */
export function validateFieldDraft(draft, ctx = {}) {
  const errors = [];
  const warnings = [];
  const {
    confirmedFieldsets = [],
    postOptionsByFieldset = {},
    scope = {},
    existingFieldsByFieldset = {},
    nativeLabelsByFieldset = {},
  } = ctx;

  // 1. Name
  const name = (draft.name ?? '').trim();
  const fieldset = normalizeFieldset(draft.fieldset);
  const postOptions = fieldset ? postOptionsByFieldset[fieldset] : null;
  const nameMax = findSystemField(postOptions, 'name')?.maximumLength || 200;
  if (!name) errors.push('The field needs a name.');
  else if (name.length > nameMax) errors.push(`The name is ${name.length} characters — FMX allows at most ${nameMax}.`);

  // 2. Fieldset
  if (!fieldset) {
    errors.push(`“${draft.fieldset}” is not an FMX fieldset.`);
  } else if (!confirmedFieldsets.includes(fieldset)) {
    errors.push(`${fieldsetLabel(fieldset)} is not one of the fieldsets confirmed for this session.`);
  }

  // 3. Field type
  const fieldType = normalizeFieldType(draft.fieldType);
  if (!fieldType) errors.push(`“${draft.fieldType}” is not an FMX field type.`);

  // 4. Scoping keys — every ID must exist in the live ID→name maps; keys the
  //    live post-options marks required must be present. IDs come from the
  //    scope screen but are still verified: the map is live, the scope choice
  //    might be stale (e.g. a request type deleted since the draft was saved).
  if (fieldset && postOptions) {
    // A field may narrow its fieldset's scope to the request types whose
    // sections actually asked it (rule R2) — the override wins per key, and
    // its IDs are verified against the same live map.
    const fieldsetScope = { ...(scope[fieldset] || {}), ...(draft.scopeOverride || {}) };
    for (const sf of postOptions.systemFields || []) {
      if (!isScopingKey(sf.key)) continue;
      const ids = scopeIdsOf(fieldsetScope[sf.key]);
      if (sf.isRequired && ids.length === 0) {
        errors.push(`${fieldsetLabel(fieldset)} requires a ${sf.label || sf.key} — pick one on the scope screen.`);
      }
      const optionsMap = sf.options || {};
      for (const id of ids) {
        if (!(String(id) in optionsMap)) {
          errors.push(`The selected ${sf.label || sf.key} (${id}) no longer exists on the FMX site — the site changed since scoping. Re-pick it on the scope screen.`);
        }
      }
    }
    for (const key of Object.keys(fieldsetScope)) {
      if (scopeIdsOf(fieldsetScope[key]).length === 0) continue;
      if (!findSystemField(postOptions, key)) {
        errors.push(`${fieldsetLabel(fieldset)} does not accept ${key} scoping.`);
      }
    }
  }

  // 5. Behavior flags gated by type. Changing a field's type can strand a
  //    flag that was legal under the old type, so this always re-checks.
  if (fieldType) {
    for (const [flag, allowedTypes] of Object.entries(FLAGS_BY_TYPE)) {
      if (draft[flag] === true && !allowedTypes.includes(fieldType)) {
        errors.push(`“${flag}” is not available on a ${fieldTypeLabel(fieldType)} field.`);
      }
      const sf = findSystemField(postOptions, flag);
      if (draft[flag] === true && sf && sf.isPermitted === false) {
        errors.push(`FMX does not permit “${flag}” on ${fieldsetLabel(fieldset)} fields.`);
      }
    }
  }

  // 6. Options
  const options = Array.isArray(draft.options) ? draft.options : [];
  const defaults = Array.isArray(draft.defaults) ? draft.defaults : [];
  if (fieldType && OPTION_TYPES.includes(fieldType)) {
    if (options.length === 0) errors.push('A drop down list needs at least one option.');
    const seen = new Map();
    for (const opt of options) {
      const key = String(opt).trim().toLowerCase();
      if (seen.has(key)) errors.push(`Duplicate option “${opt}”.`);
      else seen.set(key, opt);
    }
    if (draft.allowOtherOption && seen.has('other')) {
      errors.push('Remove the literal “Other” option — “Allow other option” already adds it.');
    }
    for (const d of defaults) {
      if (!seen.has(String(d).trim().toLowerCase())) {
        errors.push(`Default “${d}” is not one of the options.`);
      }
    }
    if (options.length > 50) {
      warnings.push(`This drop down has ${options.length} options — double-check it isn’t a lookup list (buildings, users, …) that FMX already tracks.`);
    }
  } else if (fieldType) {
    if (options.length > 0) errors.push(`Options only apply to drop down lists, not ${fieldTypeLabel(fieldType)} fields.`);
    if (defaults.length > 1) errors.push(`A ${fieldTypeLabel(fieldType)} field can have at most one default.`);
  }

  // 7. The two "already exists" screens re-run on every edit — renaming a
  //    field in the builder could collide with a native field or an existing
  //    custom field.
  if (name && fieldset) {
    const native = matchNativeField(name, nativeLabelsByFieldset[fieldset] || []);
    if (native) {
      errors.push(`FMX already captures “${name}” natively (${native.mapsTo}) — do not create it as a custom field.`);
    }
    const dupe = screenAgainstExisting(name, existingFieldsByFieldset[fieldset] || []);
    if (dupe.verdict === 'duplicate') {
      errors.push(`A custom field named “${dupe.matches[0].name}” already exists on this site’s ${fieldsetLabel(fieldset)} — skip it or rename this one.`);
    } else if (dupe.verdict === 'similar') {
      warnings.push(`Similar existing field on this site: “${dupe.matches[0].name}”. Review before creating.`);
    }
  }

  // 8. Visibility formula (rules R5/R6). The formula is NOT part of the POST
  //    body — FMX's API can't set it (CustomFieldEditModel has no such
  //    property), so it's applied by hand afterwards. A broken formula
  //    therefore WARNS rather than blocking the push: the fields are still
  //    correct, it's the copy-paste step that needs attention. An ambiguous
  //    reference is the exception — it means duplicate names, which is
  //    already an error above.
  if (draft.visibilityFormula) {
    const v = validateFormula(draft.visibilityFormula, {
      fieldset,
      draftFields: ctx.draftFields || [],
      selfName: draft.name,
    });
    for (const e of v.errors) warnings.push(`Visibility formula: ${e}`);
    for (const w of v.warnings) warnings.push(`Visibility formula: ${w}`);
  }

  // 9. Housekeeping
  if (draft.sortPosition !== null && draft.sortPosition !== undefined
      && (!Number.isInteger(draft.sortPosition) || draft.sortPosition < 0)) {
    errors.push('Sort position must be a whole number.');
  }

  return { errors, warnings };
}

// Validate a whole draft batch. Adds the within-batch name-uniqueness check
// (check 1) on top of per-field validation. Returns a Map of
// draftId → { errors, warnings }.
export function validateDraftBatch(drafts, ctx = {}) {
  const results = new Map();
  const collisions = findBatchNameCollisions(drafts);
  // Formula references resolve against the whole batch, so pass it down.
  const batchCtx = { ...ctx, draftFields: ctx.draftFields || drafts || [] };
  for (const draft of drafts || []) {
    const r = validateFieldDraft(draft, batchCtx);
    if (collisions.has(draft.id)) {
      // Duplicate names survive merging only when the occurrences genuinely
      // disagree (different options or types — rule R7). Say what to do about
      // it: in FMX this is normally ONE field scoped to several request types,
      // and duplicated names also make every visibility formula that
      // references them resolve unpredictably (CustomField.cs:432-434).
      const n = collisions.get(draft.id).length + 1;
      r.errors.push(
        `“${draft.name}” appears ${n} times in ${fieldsetLabel(draft.fieldset)}. In FMX this should be one field scoped to the request types that ask it — merge them, or rename if they really are different questions.`
      );
    }
    results.set(draft.id, r);
  }
  return results;
}
